/**
 * ProjectMaterialListsTab — new tab on the Doserne Project Detail page.
 * Lists every material list attached to this project, grouped by day.
 * Clicking a list opens the shared MaterialListModal for editing/tick-off.
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, ClipboardList, Check, Calendar, Package, Plus } from "lucide-react";
import MaterialListModal from "./MaterialListModal";
import MaterialListSourcePicker from "./MaterialListSourcePicker";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function fmtDay(iso) {
  if (!iso) return "";
  try {
    return new Date(iso + "T00:00:00").toLocaleDateString("no-NO", {
      weekday: "long", day: "numeric", month: "short", year: "numeric",
    });
  } catch { return iso; }
}

export default function ProjectMaterialListsTab({ projectId, authHeader, isAdmin }) {
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [openListId, setOpenListId] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [pickerOpen, setPickerOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [ml, sum] = await Promise.all([
          axios.get(`${API}/doserne/projects/${projectId}/material-lists`, authHeader()),
          axios.get(`${API}/doserne/projects/${projectId}/lager-summary`, authHeader()),
        ]);
        if (!cancelled) {
          setDays(ml.data.days || []);
          setMaterials(sum.data.materials || []);
        }
      } catch {
        if (!cancelled) toast.error("Kunne ikke laste lister");
      }
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [projectId, authHeader, refreshKey]);

  const closeModal = () => {
    setOpenListId(null);
    setRefreshKey((k) => k + 1);
  };
  const onPickerCreated = (list) => { setPickerOpen(false); setOpenListId(list.id); setRefreshKey((k) => k + 1); };
  const onPickerExisting = (list) => { setPickerOpen(false); setOpenListId(list.id); };

  const CreateBar = (
    <div className="flex items-center justify-between gap-2">
      <div className="text-xs uppercase tracking-widest font-black text-slate-500">Liste materiale</div>
      <button
        onClick={() => setPickerOpen(true)}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-slate-900 hover:bg-slate-800 text-white text-sm font-black"
        data-testid="project-create-list-btn"
      >
        <Plus className="w-4 h-4" /> Creează listă
      </button>
    </div>
  );

  const Picker = (
    <MaterialListSourcePicker
      open={pickerOpen}
      projectId={projectId}
      authHeader={authHeader}
      onCancel={() => setPickerOpen(false)}
      onCreated={onPickerCreated}
      onPickExisting={onPickerExisting}
    />
  );

  const Modal = openListId && (
    <MaterialListModal
      listId={openListId}
      authHeader={authHeader}
      canDelete={isAdmin}
      onClose={closeModal}
      onDeleted={closeModal}
    />
  );

  if (loading) return (
    <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
  );

  if (days.length === 0 && materials.length === 0) {
    return (
      <div className="space-y-4" data-testid="project-lists-tab">
        {CreateBar}
        <div className="bg-white border border-slate-200 rounded-2xl p-8 text-center" data-testid="lists-empty">
          <ClipboardList className="w-10 h-10 text-slate-300 mx-auto mb-2" />
          <div className="text-sm font-black text-slate-700">Nicio listă atașată încă.</div>
          <div className="text-xs text-slate-500 mt-1">
            Apasă <strong>Creează listă</strong> sau adaugă din <strong>Daglig plan</strong>.
          </div>
        </div>
        {Picker}
        {Modal}
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="project-lists-tab">
      {CreateBar}
      {materials.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden" data-testid="project-materials-summary">
          <div className="flex items-center gap-2 px-4 py-2.5 bg-cyan-50 border-b border-cyan-100">
            <Package className="w-4 h-4 text-cyan-700" />
            <div className="text-xs uppercase tracking-widest font-black text-cyan-800">Materiale pe proiect</div>
            <span className="ml-auto text-[10px] font-mono text-slate-400">{materials.length} art.</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[480px]">
              <thead className="bg-slate-50 text-[10px] uppercase tracking-widest text-slate-500 font-bold border-b border-slate-200">
                <tr>
                  <th className="text-left px-3 py-2">Articol</th>
                  <th className="text-center px-3 py-2" title="Planificat în liste">Planificat</th>
                  <th className="text-center px-3 py-2" title="Rezervat (cereri deschise)">Rezervat</th>
                  <th className="text-center px-3 py-2" title="Livrat pe șantier">Pe șantier</th>
                </tr>
              </thead>
              <tbody>
                {materials.map((m) => (
                  <tr key={m.item_id} className="border-b border-slate-100 last:border-0" data-testid={`project-mat-${m.item_id}`}>
                    <td className="px-3 py-2 font-bold text-slate-800">{m.item_name || "—"}</td>
                    <td className="px-3 py-2 text-center text-slate-600">{m.planned || 0} {m.unit}</td>
                    <td className="px-3 py-2 text-center"><span className={m.reserved > 0 ? "text-amber-700 font-black" : "text-slate-400"}>{m.reserved || 0}</span></td>
                    <td className="px-3 py-2 text-center"><span className={m.on_site > 0 ? "text-emerald-700 font-black" : "text-slate-400"}>{m.on_site || 0}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {days.map(({ date, lists }) => (
        <div key={date} className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
          <div className="flex items-center gap-2 px-4 py-2.5 bg-slate-50 border-b border-slate-200">
            <Calendar className="w-4 h-4 text-slate-500" />
            <div className="text-xs uppercase tracking-widest font-black text-slate-700">
              {fmtDay(date)}
            </div>
            <span className="ml-auto text-[10px] font-mono text-slate-400">
              {lists.length} list{lists.length === 1 ? "ă" : "e"}
            </span>
          </div>
          <ul className="divide-y divide-slate-100">
            {lists.map((lst) => {
              const total = (lst.items || []).filter((i) => i.text?.trim()).length;
              const prepared = (lst.items || []).filter((i) => i.prepared && i.text?.trim()).length;
              const allDone = total > 0 && prepared === total;
              return (
                <li key={lst.id}>
                  <button
                    type="button"
                    onClick={() => setOpenListId(lst.id)}
                    className="w-full flex items-center gap-3 px-4 py-3 hover:bg-amber-50 active:bg-amber-100 transition text-left"
                    data-testid={`list-row-${lst.id}`}
                  >
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${allDone ? "bg-emerald-500 text-white" : total === 0 ? "bg-slate-100 text-slate-400" : "bg-amber-100 text-amber-700"}`}>
                      {allDone ? <Check className="w-4 h-4" strokeWidth={3} /> : <ClipboardList className="w-4 h-4" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-black text-slate-900 truncate">
                        {lst.title || "(fără titlu)"}
                      </div>
                      <div className="text-[11px] text-slate-500 mt-0.5">
                        {total === 0 ? "Fără articole" : `${prepared}/${total} pregătit${prepared === 1 ? "" : "e"}`}
                        {lst.created_by_name && ` · de ${lst.created_by_name}`}
                      </div>
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      ))}

      {Picker}
      {Modal}
    </div>
  );
}
